import { useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

interface ImageFieldProps {
  value: string;
  onChange: (url: string) => void;
  folder?: string;
}

const MAX_SIZE = 5 * 1024 * 1024;

export default function ImageField({ value, onChange, folder = "general" }: ImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file: File) => {
    setError("");
    if (!file.type.startsWith("image/")) {
      setError("Selecciona un archivo de imagen válido.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("La imagen no debe superar 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
      const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("media")
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from("media").getPublicUrl(path);
      onChange(data.publicUrl);
    } catch {
      setError("No pudimos subir la imagen. Intenta nuevamente.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-start gap-3">
        <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-300 bg-background-100">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover object-top" />
          ) : (
            <i className="ri-image-line text-2xl leading-none text-foreground-400" aria-hidden="true"></i>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm font-label font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className={`${uploading ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} text-base leading-none`} aria-hidden="true"></i>
              {uploading ? "Subiendo…" : "Subir imagen"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm font-label font-medium text-foreground-600 transition-colors hover:bg-background-100"
              >
                <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
                Quitar
              </button>
            )}
          </div>
          <input
            type="text"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="O pega una URL (imagen o video)"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          {error && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-primary-700">
              <i className="ri-error-warning-line text-sm leading-none" aria-hidden="true"></i>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}